import type React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import useEventCallback from "hooks/useEventCallback";
import useAreaChart from "../AreaChartProvider/useAreaChartStore";
import useDrawingActions from "../DrawingProvider/useDrawingActions";
import useShowPointsTooltip from "../hooks/useShowPointsTooltip";
import useTooltip from "../TooltipProvider/useTooltipStore";
import useXAxis from "../XAxisProvider/useXAxisStore/useXAxisStore";
import useXAxisActions from "../XAxisProvider/useXAxisActions";
import FocusablePoint from "./FocusablePoint";

const NAVIGATION_KEYS = ["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown", "Home", "End"];

export interface FocusablePointsProps {
  tooltipId: string;
  getPointLabel: (date: Date) => string;
}

/** A single tab stop over the chart's dates; arrow keys move between them and open the tooltip for each. */
function FocusablePoints(props: FocusablePointsProps) {
  const { tooltipId, getPointLabel } = props;
  const { height, margin } = useAreaChart();
  const { xAxis } = useXAxis();
  const { getXScale } = useXAxisActions();
  const { tooltipOpen, hideTooltip } = useTooltip();
  const { handleClearCoordinates } = useDrawingActions();
  const showPointsTooltip = useShowPointsTooltip();

  const [activeIndex, setActiveIndex] = useState(0);
  const targetsRef = useRef<(SVGRectElement | null)[]>([]);

  const xScale = getXScale();

  // Axis data is sorted and repeats a date once per series, so keep its first occurrence.
  const points = useMemo(
    () =>
      xAxis.data
        .map((date, axisIndex) => ({ date, axisIndex }))
        .filter(({ date, axisIndex }) => axisIndex === 0 || xAxis.data[axisIndex - 1].getTime() !== date.getTime())
        .map(({ date, axisIndex }) => ({ date, axisIndex, x: xScale(date), label: getPointLabel(date) })),
    [getPointLabel, xAxis.data, xScale]
  );

  const focusIndex = Math.min(activeIndex, points.length - 1);
  const plotHeight = Math.max(0, height - margin.top - margin.bottom);

  const registerTarget = useCallback((index: number, el: SVGRectElement | null) => {
    targetsRef.current[index] = el;
  }, []);

  const hide = useCallback(() => {
    hideTooltip();
    handleClearCoordinates();
  }, [handleClearCoordinates, hideTooltip]);

  // Event callbacks stay stable as focus and the tooltip change, so only the points whose props change re-render.
  const handlePointFocus = useEventCallback((index: number) => {
    setActiveIndex(index);
    showPointsTooltip(points[index].date, points[index].axisIndex);
  });

  const handlePointBlur = useEventCallback((e: React.FocusEvent<SVGRectElement>) => {
    if (targetsRef.current.includes(e.relatedTarget as SVGRectElement)) return;
    hide();
  });

  const handlePointKeyDown = useEventCallback((e: React.KeyboardEvent<SVGRectElement>, index: number) => {
    if (e.key === "Escape") {
      if (!tooltipOpen) return;
      // Keeps an enclosing dialog open while only the tooltip is dismissed.
      e.stopPropagation();
      hide();
      return;
    }

    if (!NAVIGATION_KEYS.includes(e.key)) return;
    e.preventDefault();

    const lastIndex = points.length - 1;
    const step = e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 1;
    const nextIndex = e.key === "Home" ? 0 : e.key === "End" ? lastIndex : index + step;
    targetsRef.current[Math.max(0, Math.min(nextIndex, lastIndex))]?.focus();
  });

  return (
    <g>
      {points.map((point, i) => (
        <FocusablePoint
          key={point.date.getTime()}
          index={i}
          x={point.x}
          y={margin.top}
          height={plotHeight}
          label={point.label}
          isActive={i === focusIndex}
          describedBy={i === focusIndex && tooltipOpen ? tooltipId : undefined}
          registerTarget={registerTarget}
          onPointFocus={handlePointFocus}
          onPointBlur={handlePointBlur}
          onPointKeyDown={handlePointKeyDown}
        />
      ))}
    </g>
  );
}

export default FocusablePoints;
